// React
import { memo, useCallback, useEffect, useState } from "react";

// UI Components
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";

// Helpers & Utilities
import { useTranslate } from "helpers";

// Services & API
import { getVehicleServiceAPI } from "services/api/calculations/vehicleservice/api";

// Types
import { VehicleServicesDetailFormValues } from "services/api/calculations/vehicleservice/types";
import { VehicleServicesDetailProps } from "./types";
import { Errors } from "services/forms/validation/types";

// Constants
import { CALCULATION_VEHICLE_SERVICES_EDIT_PAGE_TITLE } from "ui/components/PageTitle/consts";

// Components
import VehicleServicesEditCard from "../../../VehicleServicesEditCard/VehicleServicesEditCard";

// Constants
import { initialVehicleServicesDetailForm } from "services/api/calculations/vehicleservice/consts";

const VehicleServicesDetailCard: React.FC<VehicleServicesDetailProps> = (
  props
) => {
  const { vehicleServicesId } = props;

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<VehicleServicesDetailFormValues>(
    initialVehicleServicesDetailForm
  );

  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    if (vehicleServicesId) {
      fetchVehicleServices(vehicleServicesId);
    }
  }, [vehicleServicesId]);

  const fetchVehicleServices = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialVehicleServicesDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getVehicleServiceAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const handleEditSuccess = useCallback(() => {
    if (vehicleServicesId) fetchVehicleServices(vehicleServicesId);
    setShowEditModal(false);
  }, []);

  if (!vehicleServicesId) return <></>;

  return (
    <>
      <div className="  flex justify-center ">
        <div className="rounded-sm bg-white border max-w-lg w-full">
          <div className="divide-y divide-slate-200">
            <div className="flex justify-between items-center">
              <p className="text-sm font-semibold text-slate-600 px-4 py-3 uppercase">
                {t("admin.calculation.vehicleservices.detail.title")}
              </p>
              <div className="flex items-center gap-2 px-3 my-3 ">
                <Button
                  size="small"
                  variant="primary"
                  children={t("admin.change")}
                  onClick={() => setShowEditModal(true)}
                  startIcon="/media/icons/plus.svg"
                />
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.vehicleservices.detail.name")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.name}
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.vehicleservices.detail.rateperkm")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.ratePerKm} %
              </div>
            </div>
          </div>
        </div>
      </div>
      {showEditModal && (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <ModalHeader
            title={CALCULATION_VEHICLE_SERVICES_EDIT_PAGE_TITLE.text}
            onHide={() => setShowEditModal(false)}
          />
          <ModalBody>
            <VehicleServicesEditCard
              vehicleServicesId={vehicleServicesId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(VehicleServicesDetailCard);
